// Hand-written drift guard for the `results_payload` wire contract.
//
// Runs at the WS handler boundary to surface shape drift early. By design:
//   - WARNS via console + structured event, never throws or blocks the UI.
//   - Returns a best-effort normalized payload so the caller can still
//     dispatch partial data (e.g. deals arrived but researchDocs drifted).
//
// When adding new top-level fields to the payload, extend
// `validateResultsPayload` so drifts are caught the moment they land instead
// of surfacing as mystery bugs in downstream reducers/components.

export interface ValidationResult {
  ok: boolean;
  warnings: string[];
  // Best-effort normalized arrays. Typed as `unknown[]` since drift is
  // possible — callers should cast to the expected shape (e.g. `Deal[]`,
  // `Document[]`) before forwarding to setResults.
  results: unknown[];
  documents: unknown[];
  researchDocs: unknown[];
  experts: unknown[];
  facts: unknown[];
}

// Either alternative is acceptable — the registry normalises `docId` →
// `documentId` on insertion, so a payload carrying only one key is fine.
const REQUIRED_DOC_FIELD_ALTERNATIVES = [['docId', 'documentId']] as const;
const REQUIRED_RESEARCH_DOC_FIELD_ALTERNATIVES = [['docId', 'documentId']] as const;
const REQUIRED_DEAL_FIELD_ALTERNATIVES: readonly (readonly string[])[] = []; // deals have no strict required field — many are optional at the source

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function warnMissingAlternatives(
  warnings: string[],
  collection: string,
  index: number,
  alternatives: readonly string[]
): void {
  const label =
    alternatives.length === 1
      ? `"${alternatives[0]}"`
      : alternatives.map((a) => `"${a}"`).join(' or ');
  warnings.push(
    `results_payload.${collection}[${index}] missing required field ${label}`
  );
}

function coerceArray(
  value: unknown,
  label: string,
  warnings: string[]
): unknown[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    warnings.push(`results_payload.${label} is not an array (got ${typeof value}); coercing to []`);
    return [];
  }
  return value;
}

function checkRequiredFieldAlternatives(
  items: unknown[],
  requiredAlternatives: readonly (readonly string[])[],
  label: string,
  warnings: string[]
): void {
  items.forEach((item, idx) => {
    if (!isPlainObject(item)) {
      warnings.push(`results_payload.${label}[${idx}] is not an object`);
      return;
    }
    for (const alternatives of requiredAlternatives) {
      const hasAny = alternatives.some(
        (field) => item[field] !== undefined && item[field] !== null
      );
      if (!hasAny) {
        warnMissingAlternatives(warnings, label, idx, alternatives);
      }
    }
  });
}

/**
 * Validate a `results_payload` envelope. Returns a `ValidationResult` with
 * any warnings (empty when the payload is clean) and normalized arrays
 * callers can safely consume. Never throws.
 */
export function validateResultsPayload(payload: unknown): ValidationResult {
  const warnings: string[] = [];

  if (!isPlainObject(payload)) {
    warnings.push(`results_payload root is not an object (got ${typeof payload})`);
    return { ok: false, warnings, results: [], documents: [], researchDocs: [], experts: [], facts: [] };
  }

  const results = coerceArray(payload.results, 'results', warnings);
  const documents = coerceArray(payload.documents, 'documents', warnings);
  const researchDocs = coerceArray(payload.researchDocs, 'researchDocs', warnings);
  const experts = coerceArray(payload.experts, 'experts', warnings);
  const facts = coerceArray(payload.facts, 'facts', warnings);

  checkRequiredFieldAlternatives(results, REQUIRED_DEAL_FIELD_ALTERNATIVES, 'results', warnings);
  checkRequiredFieldAlternatives(documents, REQUIRED_DOC_FIELD_ALTERNATIVES, 'documents', warnings);
  checkRequiredFieldAlternatives(researchDocs, REQUIRED_RESEARCH_DOC_FIELD_ALTERNATIVES, 'researchDocs', warnings);

  return {
    ok: warnings.length === 0,
    warnings,
    results,
    documents,
    researchDocs,
    experts,
    facts,
  };
}

/**
 * Runs validation and logs a single structured warning if drift is detected.
 * Use this at WS-handler boundaries so drift surfaces once per payload, not
 * per downstream consumer.
 */
export function logResultsPayloadDrift(
  payload: unknown,
  context: { turnId?: string; source?: string } = {}
): ValidationResult {
  const result = validateResultsPayload(payload);
  if (!result.ok) {
    console.warn('[results_payload drift]', {
      ...context,
      warnings: result.warnings,
    });
  }
  return result;
}
